import { z } from "zod";
import { useForm } from "@tanstack/react-form";
import { useCurrentUser } from "src/business-hooks/useCurrentUser";
import { parseModalSectionParam } from "src/components/general/ModalWithNavigation/ModalWithNavigation.utils";
import { useSearchParamState } from "src/hooks/useSearchParams";
import { useTestControlPlaneMutation } from "src/queries/platform/system.mutation";
import {
  useQuerySystemSettings,
  useQuerySystemSettingsSchema,
} from "src/queries/platform/system.query";
import { useCommonMutation } from "src/utils/mutations";
import { SYSTEM_SETTINGS_SEARCH_PARAM } from "./SystemSettings.constants";

interface SystemSettingsResponse {
  properties: Record<string, BaseSystemSetting>;
  title: string;
  type: string;
}

type SystemSettingValue = string | number | boolean;

interface BaseSystemSetting {
  default: SystemSettingValue;
  group: string;
  subgroup?: string;
  title: string;
  type: "integer" | "number" | "string" | "boolean";
  description?: string;
  is_hidden?: boolean;
  is_secret?: boolean;
  is_system?: boolean;
  enum?: string[];
  input_type?: string;
}

const select = (settings: SystemSettingsResponse) => {
  if (!settings.properties) return settings;

  return {
    ...settings,
    // order settings by their groups
    properties: Object.fromEntries(
      Object.entries(settings.properties).sort(([, a], [, b]) =>
        (a.group || "zzz").localeCompare(b.group || "zzz")
      )
    ) as Record<string, BaseSystemSetting>,
  } as const;
};

// Dynamically create Zod schema from API schema
const createValidationSchema = (schemaData?: SystemSettingsResponse) => {
  if (!schemaData?.properties) {
    return z.object({}).partial();
  }

  const shape: Record<string, z.ZodTypeAny> = {};

  for (const [key, setting] of Object.entries(schemaData.properties)) {
    if (typeof setting === "boolean") continue;

    let fieldSchema: z.ZodTypeAny;

    switch (setting.type) {
      case "integer":
        fieldSchema = z
          .number({ message: `${setting.title} must be an integer` })
          .int();
        break;
      case "number":
        fieldSchema = z.number({ message: `${setting.title} must be a number` });
        break;
      case "boolean":
        fieldSchema = z.boolean();
        break;
      case "string":
        if (setting.enum) {
          fieldSchema = z.enum(setting.enum as [string, ...string[]]);
        } else {
          fieldSchema = z.string();
        }
        break;
      default:
        fieldSchema = z.any();
    }

    shape[key] = fieldSchema;
  }

  return z.object(shape).partial();
};

export const useSystemSettings = () => {
  const user = useCurrentUser();
  const [value, setValue] = useSearchParamState(SYSTEM_SETTINGS_SEARCH_PARAM);
  const isOpen = parseModalSectionParam(value).isOpen;
  const systemSettingsQuery = useQuerySystemSettings({
    enabled: isOpen && !!user.is_tenant_admin,
  });

  const schemaQuery = useQuerySystemSettingsSchema({
    select: select as (data: unknown) => ReturnType<typeof select>,
    enabled: isOpen && !!user.is_tenant_admin,
  });

  const systemSettingsData = systemSettingsQuery.data;
  const schemaData = schemaQuery.data;

  const systemSettingsMutation = useCommonMutation<unknown>({
    url: `/api/system-settings`,
    method: "PUT",
    successMessage: "Saved system settings",
    failureMessage: "Failed saving system settings",
  });

  const validationSchema = createValidationSchema(schemaData);

  const form = useForm({
    onSubmit: async ({ value, formApi }) => {
      try {
        await systemSettingsMutation.mutateAsync(value);
      } catch {
        return; // the mutation already notified; keep the edits so the user can retry
      }
      // The saved values become the new baseline, so the form reads clean again.
      formApi.reset(value);
    },
    defaultValues: systemSettingsData,
    validators: {
      onChange: validationSchema,
    },
  });

  const sendTestEmail = useCommonMutation<void>({
    url: `/api/system-settings/test-email`,
    method: "POST",
    successMessage: "Test email sent",
    failureMessage: "Failed sending test email",
  });

  // The SMTP test verifies the global mail config, which only tenant 1 can set —
  // the backend restricts /test-email to tenant-1 admins accordingly. The schema
  // only exposes the system (mail) fields to tenant 1, so their presence is the
  // signal for whether to offer the button at all.
  const canSendTestEmail = Boolean(schemaData?.properties?.test_email);

  // Tests the saved Control Plane Connection settings; the toast says what to fix.
  const testControlPlane = useTestControlPlaneMutation();
  // Same gate as the test email: the fields only exist for tenant 1.
  const canTestControlPlane = Boolean(schemaData?.properties?.control_plane_enabled);

  return {
    isOpen,
    value,
    setValue,
    schemaQuery,
    systemSettingsQuery,
    form,
    sendTestEmail,
    canSendTestEmail,
    testControlPlane,
    canTestControlPlane,
  };
};

export type SystemSettingsForm = ReturnType<typeof useSystemSettings>["form"];
